/**
 * muxProcessingWatcher — FIX-2. The ONE bounded way this app waits
 * for a Mux transcode.
 *
 * WHY THIS EXISTS: before FIX-2 there were two hand-rolled
 * setInterval polls (DeliveryDetail's upload queue and the gallery
 * upload manager) and both of them could trap a file forever:
 *
 *   • DeliveryDetail's poll had NO attempt cap and lived inside an
 *     `await new Promise` that only resolved on ready/errored. Wrong
 *     Mux credentials, a stalled asset or a silent webhook meant the
 *     tile sat on "Finalizing…" until the tab closed — AND, because
 *     the batch loop awaited it, every file behind it never started.
 *   • The gallery manager's poll gave up after ~12 minutes and then
 *     reported SUCCESS for a video that had never processed.
 *
 * The rules encoded here:
 *   1. Every wait is bounded — by wall-clock deadline AND by attempt
 *      count. There is no code path that polls forever.
 *   2. A wait NEVER reports success it didn't observe. The terminal
 *      outcomes are ready / errored / timeout / missing / cancelled,
 *      and only `ready` means "this video is playable".
 *   3. `timeout` is NOT failure. A 6-minute 4K transcode is normal,
 *      so the timeout copy says "still processing, it will appear on
 *      its own" and hands the user a re-check, never a red X.
 *   4. Nothing here throws. Every await is wrapped; the worst case is
 *      a resolved result with `syncUnreachable: true`.
 *
 * THE RESCUE PATH: every tick calls a sync edge function BEFORE
 * reading the row. That function pulls the truth straight from the Mux
 * API with the service role, so it completes uploads even when
 * `MUX_WEBHOOK_SECRET` is unset and `mux-webhook` is (correctly)
 * refusing every unsigned request with a 503. This poll is therefore
 * the client's real completion path, not a backstop.
 *
 * FIX-3 — GENERALIZED, NOT FORKED. This file used to be hardcoded to
 * `delivery_files` + `sync-mux-status`. Website videos live in
 * `studio_site_media` and had no rescue path at all, so rather than
 * copy 250 lines the loop now runs against a `MuxWatchTarget`:
 *
 *   { kind: 'delivery_file' } → sync-mux-status        → delivery_files
 *   { kind: 'site_media' }    → sync-site-media-status → studio_site_media
 *
 * The two public FIX-2 entry points (`watchMuxProcessing`,
 * `checkMuxOnce`) keep their EXACT original signatures and behaviour —
 * they are now thin wrappers over the generic loop — so DeliveryDetail,
 * galleryUploadManager and VideoProcessingTile are untouched.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  syncMuxStatus,
  syncSiteMediaStatus,
  isSiteMediaNotFound,
} from '@/services/muxRecoveryService';

/** How often we ask Mux (via sync-mux-status) for the truth. */
export const MUX_POLL_INTERVAL_MS = 6000;
/** Below this, transcoding is unremarkable — say nothing alarming. */
export const MUX_TRANSCODE_REASSURE_MS = 2 * 60 * 1000;
/**
 * Hard stop. Mux transcodes are typically well under real time, but a
 * long 4K master can legitimately run several minutes, so 20 minutes
 * is generous on purpose — it exists to end the spinner, not to
 * declare failure.
 */
export const MUX_TRANSCODE_DEADLINE_MS = 20 * 60 * 1000;

export type MuxWatchOutcome =
  /** Mux says the asset is playable. The ONLY success. */
  | 'ready'
  /** Mux says the asset failed to encode. Real, permanent failure. */
  | 'errored'
  /** We stopped waiting. Undecided — may still finish on its own. */
  | 'timeout'
  /** The backing row disappeared (deleted mid-transcode). */
  | 'missing'
  /** The caller aborted (user cancel, or the page unmounted). */
  | 'cancelled';

export interface MuxWatchResult {
  outcome: MuxWatchOutcome;
  /** Plain-language sentence: what happened + what to do next. */
  message: string;
  elapsedMs: number;
  /**
   * True when EVERY sync call failed. That means the rescue path is
   * down (bad Mux credentials, function not deployed, network), so a
   * `timeout` here is much more suspicious than a timeout where sync
   * was answering happily.
   */
  syncUnreachable: boolean;
  /**
   * FIX-3 — the most recent SPECIFIC complaint the sync function made
   * about this row (`asset_errored`, `no_mux_reference`,
   * `mux_not_configured`, …). Present only when the backend named a
   * real problem; undefined otherwise. Callers should prefer this over
   * `message` when it exists, because it says WHY.
   */
  syncErrorDetail?: string;
}

/**
 * The reassuring half of the state machine. Long transcodes are
 * legitimate, so the copy stays calm and only gets more specific as
 * time passes — it never implies a problem.
 */
export function transcodeHint(elapsedMs: number): string {
  if (elapsedMs < MUX_TRANSCODE_REASSURE_MS) {
    return 'Transcoding — usually under a minute.';
  }
  const minutes = Math.max(2, Math.floor(elapsedMs / 60000));
  return `Still transcoding (${minutes} min). Large 4K files normally take several minutes.`;
}

/** Human copy for a finished watch. Never "something went wrong". */
export function muxOutcomeMessage(
  outcome: MuxWatchOutcome,
  elapsedMs: number,
  syncUnreachable: boolean,
): string {
  switch (outcome) {
    case 'ready':
      return 'Ready to play.';
    case 'errored':
      return 'Mux could not process this file. Re-export it as an H.264 MP4 or MOV and upload it again.';
    case 'missing':
      return 'This video was removed while it was still processing. Upload it again if you still need it.';
    case 'cancelled':
      return 'Stopped waiting for this video.';
    case 'timeout':
    default: {
      const minutes = Math.max(1, Math.round(elapsedMs / 60000));
      if (syncUnreachable) {
        return `Your file uploaded, but we couldn't reach the video service to confirm it finished (${minutes} min). It may still appear on its own — press Check again in a minute, and re-upload if it never shows up.`;
      }
      return `Uploaded. Mux still hasn't reported this one ready after ${minutes} minutes — it will appear by itself when it finishes. Press Check again for the latest status.`;
    }
  }
}

/** Statuses that mean "Mux is still working" rather than "finished". */
export function isTranscoding(status: string | null | undefined): boolean {
  return status !== 'ready' && status !== 'errored';
}

/** setTimeout as a promise that also settles the moment we're aborted. */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      window.clearTimeout(timer);
      resolve();
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// ─────────────────────────────────────────────────────────────
// FIX-3 — what we're watching
// ─────────────────────────────────────────────────────────────

/**
 * Which pipeline this video belongs to. Adding a third would mean one
 * more branch in `adapterFor` and nothing else.
 */
export type MuxWatchTarget =
  | { kind: 'delivery_file'; deliveryId: string; deliveryFileId: string }
  | { kind: 'site_media'; mediaId: string; siteId?: string | null };

/** What a poll tick learned from the row itself. */
interface RowStatus {
  /** The row is gone. Terminal. */
  missing: boolean;
  /** Normalized mux status, or null when we couldn't read it. */
  status: string | null;
}

interface TargetAdapter {
  /** Ask the backend to reconcile against Mux. Throws when unreachable. */
  sync: () => Promise<{ errorDetail?: string }>;
  /** Read the row's current status. Never throws. */
  read: () => Promise<RowStatus>;
}

function adapterFor(target: MuxWatchTarget): TargetAdapter {
  if (target.kind === 'site_media') {
    // Destructured to consts up front: TypeScript does not carry a
    // discriminated-union narrowing into the closures below when the
    // binding is a (mutable) parameter.
    const { mediaId, siteId } = target;

    // What the LAST sync call observed with the service role. That is
    // strictly better evidence than a client read, which RLS can
    // filter, so `read` prefers it.
    let syncedStatus: string | null | undefined;
    let syncedPlayback: string | null | undefined;
    let rowConfirmedGone = false;

    /** ready + a public playback id is the only usable pair. */
    const normalize = (status: string | null, playback: string | null): string | null =>
      status === 'ready' && !playback ? 'preparing' : status;

    return {
      sync: async () => {
        try {
          const res = await syncSiteMediaStatus({ mediaId, siteId: siteId ?? undefined });
          const mine = res?.media?.find((m) => m.id === mediaId);
          syncedStatus = mine ? mine.mux_status ?? null : undefined;
          syncedPlayback = mine ? mine.mux_playback_id ?? null : undefined;
          // The function reports named, per-row problems instead of
          // failing silently. Surface the one about OUR row.
          const problem = res?.errors?.find((e) => e.id === mediaId);
          return { errorDetail: problem?.detail };
        } catch (err) {
          syncedStatus = undefined;
          syncedPlayback = undefined;
          if (isSiteMediaNotFound(err)) {
            // Authoritative: the service role couldn't find it either.
            rowConfirmedGone = true;
            return { errorDetail: 'This video no longer exists.' };
          }
          throw err;
        }
      },
      read: async () => {
        if (rowConfirmedGone) return { missing: true, status: null };
        if (syncedStatus !== undefined) {
          return {
            missing: false,
            status: normalize(syncedStatus, syncedPlayback ?? null),
          };
        }
        // Fallback: read the row ourselves. NOTE the deliberate
        // asymmetry with delivery_files — an empty result here is NOT
        // reported as `missing`, because RLS filtering and deletion
        // look identical from the client and calling a live video
        // "removed" would be a lie. Undecided keeps the (bounded) loop
        // running; only the service-role 404 above says missing.
        try {
          const { data, error } = await supabase
            .from('studio_site_media')
            .select('mux_status, mux_playback_id')
            .eq('id', mediaId)
            .maybeSingle();
          if (error) {
            console.warn('[FIX-3] studio_site_media status read failed', error);
            return { missing: false, status: null };
          }
          if (!data) return { missing: false, status: null };
          const row = data as { mux_status: string | null; mux_playback_id: string | null };
          return { missing: false, status: normalize(row.mux_status ?? null, row.mux_playback_id) };
        } catch (err) {
          console.warn('[FIX-3] studio_site_media status read threw', err);
          return { missing: false, status: null };
        }
      },
    };
  }

  const { deliveryId, deliveryFileId } = target;
  return {
    sync: async () => {
      await syncMuxStatus({ deliveryId, deliveryFileId });
      return {};
    },
    read: async () => {
      try {
        // maybeSingle(), not single(): a deleted row must not throw
        // inside the poll.
        const { data, error } = await supabase
          .from('delivery_files')
          .select('mux_status')
          .eq('id', deliveryFileId)
          .maybeSingle();
        if (error) {
          console.warn('[FIX-2] delivery_files status read failed', error);
          return { missing: false, status: null };
        }
        if (!data) return { missing: true, status: null };
        return { missing: false, status: (data as { mux_status: string | null }).mux_status ?? null };
      } catch (err) {
        console.warn('[FIX-2] delivery_files status read threw', err);
        return { missing: false, status: null };
      }
    },
  };
}

/** Options shared by every watch, whatever the target is. */
export interface WatchTargetOptions {
  /** Abort to stop waiting (user cancel, unmount). */
  signal?: AbortSignal;
  intervalMs?: number;
  deadlineMs?: number;
  /**
   * Called on every tick that hasn't resolved yet, so the caller can
   * keep the "this is normal" copy fresh without owning a timer.
   */
  onTick?: (info: { elapsedMs: number; hint: string; status: string | null }) => void;
}

export interface WatchMuxOptions extends WatchTargetOptions {
  deliveryId: string;
  deliveryFileId: string;
}

export interface WatchSiteMediaOptions extends WatchTargetOptions {
  /** studio_site_media.id */
  mediaId: string;
  /** studio_sites.id — optional, only used to widen the sync sweep. */
  siteId?: string | null;
}

/**
 * Poll until Mux resolves the asset, we run out of patience, or the
 * caller aborts. Resolves — never rejects.
 *
 * FIX-2 entry point, signature unchanged. Delegates to `watchTarget`.
 */
export async function watchMuxProcessing({
  deliveryId,
  deliveryFileId,
  ...options
}: WatchMuxOptions): Promise<MuxWatchResult> {
  return watchTarget({ kind: 'delivery_file', deliveryId, deliveryFileId }, options);
}

/**
 * FIX-3 — the website-video twin. Same bounded loop, same outcomes,
 * same "never claim success we didn't observe" rule; it just watches
 * `studio_site_media` through `sync-site-media-status`.
 */
export async function watchSiteMediaProcessing({
  mediaId,
  siteId,
  ...options
}: WatchSiteMediaOptions): Promise<MuxWatchResult> {
  return watchTarget({ kind: 'site_media', mediaId, siteId }, options);
}

/**
 * The one bounded poll. Every wait in this app goes through here.
 */
export async function watchTarget(
  target: MuxWatchTarget,
  { signal, intervalMs = MUX_POLL_INTERVAL_MS, deadlineMs = MUX_TRANSCODE_DEADLINE_MS, onTick }: WatchTargetOptions = {},
): Promise<MuxWatchResult> {
  const adapter = adapterFor(target);
  const startedAt = Date.now();
  // Belt AND braces: a wall-clock deadline (the meaningful one) plus
  // a hard attempt cap, so a pathological interval of 0 or a clock
  // jump still can't produce an unbounded loop.
  const maxAttempts = Math.max(1, Math.ceil(deadlineMs / Math.max(1000, intervalMs)) + 5);

  let attempts = 0;
  let syncCalls = 0;
  let syncFailures = 0;
  let syncErrorDetail: string | undefined;

  const finish = (outcome: MuxWatchOutcome): MuxWatchResult => {
    const elapsedMs = Date.now() - startedAt;
    const syncUnreachable = syncCalls > 0 && syncFailures === syncCalls;
    return {
      outcome,
      message: muxOutcomeMessage(outcome, elapsedMs, syncUnreachable),
      elapsedMs,
      syncUnreachable,
      syncErrorDetail,
    };
  };

  try {
    while (attempts < maxAttempts) {
      if (signal?.aborted) return finish('cancelled');
      if (Date.now() - startedAt >= deadlineMs) return finish('timeout');

      attempts += 1;

      // 1. Rescue path first — this is what actually completes an
      //    upload when the webhook never fires.
      syncCalls += 1;
      try {
        const { errorDetail } = await adapter.sync();
        // Keep the LATEST named problem, and clear a stale one once
        // the backend stops complaining.
        syncErrorDetail = errorDetail;
      } catch (err) {
        syncFailures += 1;
        // Console only — a single failed sync is noise, and the loop
        // is still bounded either way.
        console.warn(`[FIX-2/3] sync failed for ${target.kind}`, err);
      }

      if (signal?.aborted) return finish('cancelled');

      // 2. Read what the row says now.
      const { missing: rowMissing, status } = await adapter.read();

      if (rowMissing) return finish('missing');
      if (status === 'ready') return finish('ready');
      if (status === 'errored') return finish('errored');

      const elapsedMs = Date.now() - startedAt;
      try {
        onTick?.({ elapsedMs, hint: transcodeHint(elapsedMs), status });
      } catch (err) {
        // A display callback must never break the poll.
        console.warn('[FIX-2] watch onTick threw', err);
      }

      if (Date.now() - startedAt >= deadlineMs) return finish('timeout');
      await delay(intervalMs, signal);
    }
    return finish('timeout');
  } catch (err) {
    // Structural surprise (module teardown, etc.). Still resolve —
    // an unhandled rejection here is exactly the class of bug FIX-2
    // exists to remove.
    console.error('[FIX-2/3] watchTarget failed unexpectedly', err);
    return finish('timeout');
  }
}

export type MuxCheckOutcome = 'ready' | 'errored' | 'processing' | 'missing' | 'unreachable';

export interface MuxCheckResult {
  outcome: MuxCheckOutcome;
  /**
   * FIX-3 — the backend's specific complaint about this row, when it
   * made one. Lets a "Check again" button say WHY instead of just
   * "still processing".
   */
  detail?: string;
}

/**
 * One-shot "check again" against any target: ask the sync function
 * once, then report the row's current status. Used by the manual
 * re-check affordances so a user is never stuck waiting on a poll they
 * can't influence. Resolves — never rejects.
 */
export async function checkTargetOnce(target: MuxWatchTarget): Promise<MuxCheckResult> {
  const adapter = adapterFor(target);
  let unreachable = false;
  let detail: string | undefined;
  try {
    const res = await adapter.sync();
    detail = res.errorDetail;
  } catch (err) {
    unreachable = true;
    console.warn(`[FIX-2/3] manual sync failed for ${target.kind}`, err);
    detail = err instanceof Error ? err.message : undefined;
  }
  try {
    const { missing, status } = await adapter.read();
    if (missing) return { outcome: 'missing', detail };
    if (status === 'ready') return { outcome: 'ready' };
    if (status === 'errored') return { outcome: 'errored', detail };
    // Still transcoding, OR we couldn't read the row at all. When the
    // sync call ALSO failed we report `unreachable`, because "still
    // processing" would be a claim we have no evidence for.
    return { outcome: unreachable ? 'unreachable' : 'processing', detail };
  } catch {
    return { outcome: 'unreachable', detail };
  }
}

/**
 * FIX-2 entry point, signature and return type unchanged — the
 * gallery + delivery tiles still get a bare outcome string.
 */
export async function checkMuxOnce(
  deliveryId: string,
  deliveryFileId: string,
): Promise<MuxCheckOutcome> {
  const { outcome } = await checkTargetOnce({
    kind: 'delivery_file',
    deliveryId,
    deliveryFileId,
  });
  return outcome;
}

/**
 * FIX-3 — one-shot check for a website video. Returns the detail as
 * well, because `sync-site-media-status` names its failures and the
 * site editor is the surface where "why?" actually matters.
 */
export async function checkSiteMediaOnce(
  mediaId: string,
  siteId?: string | null,
): Promise<MuxCheckResult> {
  return checkTargetOnce({ kind: 'site_media', mediaId, siteId });
}
